import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calculator, Star, PackagePlus, X, Plus, Trash2, Package, Truck, Plane } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, inr } from "@/lib/api";

export const BOOKING_MODES = [
  { k: "b2c", label: "B2C Parcel", icon: Package, endpoint: "rates/calculate", bookEndpoint: "orders" },
  { k: "b2b", label: "B2B Freight", icon: Truck, endpoint: "rates/b2b", bookEndpoint: "orders/b2b" },
  { k: "air", label: "Air Express", icon: Plane, endpoint: "rates/air", bookEndpoint: "orders/air" },
];

const EMPTY_ADDR = { name: "", phone: "", address: "", city: "", state: "", pincode: "" };
const EMPTY_DEST = { warehouse_name: "", contact_name: "", contact_phone: "", address_line_1: "", city: "", state: "", pincode: "" };

export const QuoteCard = ({ q, onBook, booking }) => (
  <div data-testid={`quote-${q.courier_id}`}
    className={`border-2 border-obsidian bg-white p-5 relative hover:shadow-[6px_6px_0px_0px_#0B1E3A] transition-all duration-150 ${q.recommended ? "shadow-[6px_6px_0px_0px_#2563EB]" : ""}`}>
    {q.recommended && (
      <span className="absolute -top-3 left-4 bg-acid border-2 border-obsidian px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-widest flex items-center gap-1 text-white">
        <Star className="w-3 h-3" /> Recommended
      </span>
    )}
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 flex items-center justify-center font-display font-black text-lg text-white border-2 border-obsidian" style={{ backgroundColor: q.color || "#0B1E3A" }}>
        {q.logo_letter}
      </span>
      <div>
        <p className="font-bold text-sm">{q.display_name || q.courier_name}</p>
        <p className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">{q.courier_tier || q.delivery_partner || q.mode}</p>
      </div>
    </div>
    <p className="font-display font-black text-3xl tracking-tight mt-4">{inr(q.rate)}</p>
    <div className="flex items-center justify-between mt-3 text-xs font-mono text-zinc-500">
      <span>ETA {q.eta_days}</span>
      <span className={q.cod_available ? "text-obsidian font-bold" : ""}>{q.cod_available ? "COD available" : "Prepaid only"}</span>
    </div>
    <button
      onClick={() => onBook(q)}
      data-testid={`quote-book-${q.courier_id}`}
      className={`mt-4 w-full inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest px-4 py-2.5 border-2 border-obsidian transition-all duration-150 ${
        booking?.courier_id === q.courier_id
          ? "bg-obsidian text-sky-300"
          : "bg-acid text-white shadow-[3px_3px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px]"
      }`}
    >
      <PackagePlus className="w-4 h-4" /> {booking?.courier_id === q.courier_id ? "Booking…" : "Book this courier"}
    </button>
  </div>
);

const Field = ({ label, value, onChange, ph, type = "text", max, testid, required = true }) => (
  <div>
    <label className="text-[10px] font-mono text-zinc-400 uppercase">{label}</label>
    <input type={type} required={required} maxLength={max} value={value} onChange={onChange} placeholder={ph}
      data-testid={testid} min={type === "number" ? "0.1" : undefined} step={type === "number" ? "0.1" : undefined}
      className="mt-1 w-full border-2 border-obsidian bg-paper px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
  </div>
);

const WarehouseSelect = ({ value, onChange, testid, warehouses }) => (
  <div>
    <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pickup warehouse</p>
    <select value={value} onChange={onChange} required data-testid={testid}
      className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
      {warehouses.length === 0 && <option value="">No warehouses — add one first</option>}
      {warehouses.map((w) => (
        <option key={w.id} value={w.id}>{w.address_title} — {w.city} {w.pincode}</option>
      ))}
    </select>
  </div>
);

const DeliveryFields = ({ state, setState, prefix }) => (
  <div className="space-y-3">
    {[
      { k: "name", ph: "Customer name" }, { k: "phone", ph: "Customer phone (10 digits)", max: 10 },
      { k: "address", ph: "Full address" }, { k: "city", ph: "City" },
      { k: "state", ph: "State" }, { k: "pincode", ph: "Pincode", max: 6 },
    ].map((f) => (
      <Field key={f.k} label={f.ph} value={state[f.k]} ph={f.ph} max={f.max} testid={`${prefix}-${f.k}`}
        onChange={(e) => setState((s) => ({ ...s, [f.k]: e.target.value }))} />
    ))}
  </div>
);

const PackageFields = ({ pkg, setPkg, prefix, withDesc }) => (
  <div className="grid grid-cols-2 gap-3">
    <Field label="Wt (kg)" type="number" value={pkg.weight_kg} ph="1" testid={`${prefix}-weight`} onChange={(e) => setPkg((s) => ({ ...s, weight_kg: e.target.value }))} />
    <Field label="L (cm)" type="number" value={pkg.length_cm} ph="20" testid={`${prefix}-length`} onChange={(e) => setPkg((s) => ({ ...s, length_cm: e.target.value }))} />
    <Field label="B (cm)" type="number" value={pkg.breadth_cm} ph="15" testid={`${prefix}-breadth`} onChange={(e) => setPkg((s) => ({ ...s, breadth_cm: e.target.value }))} />
    <Field label="H (cm)" type="number" value={pkg.height_cm} ph="10" testid={`${prefix}-height`} onChange={(e) => setPkg((s) => ({ ...s, height_cm: e.target.value }))} />
    <div className="col-span-2">
      <Field label="Invoice value (₹)" type="number" value={pkg.invoice_value} ph="999" testid={`${prefix}-invoice`} onChange={(e) => setPkg((s) => ({ ...s, invoice_value: e.target.value }))} />
    </div>
    {withDesc && (
      <div className="col-span-2">
        <Field label="Contents (optional)" required={false} value={pkg.description} ph="2 boxes — apparel" testid={`${prefix}-description`} onChange={(e) => setPkg((s) => ({ ...s, description: e.target.value }))} />
      </div>
    )}
  </div>
);

export default function BookingFlow({ fixedMode }) {
  const navigate = useNavigate();
  const [internalMode, setInternalMode] = useState("b2c");
  const mode = fixedMode || internalMode;
  const [form, setForm] = useState({ pickup_pincode: "", delivery_pincode: "", weight_kg: "1", payment_mode: "prepaid" });
  const [quotes, setQuotes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookingError, setBookingError] = useState("");

  const [b2c, setB2c] = useState({ warehouse_id: "", delivery: EMPTY_ADDR, package: { weight_kg: "1", length_cm: "20", breadth_cm: "15", height_cm: "10", invoice_value: "", description: "" } });
  const [air, setAir] = useState({ warehouse_id: "", delivery: EMPTY_ADDR, package: { weight_kg: "1", length_cm: "20", breadth_cm: "15", height_cm: "10", invoice_value: "" } });
  const [b2b, setB2b] = useState({ warehouse_id: "", destination: EMPTY_DEST, units: [{ units: "1", weight: "10", length: "40", width: "30", height: "30" }], invoice_value: "" });

  useEffect(() => {
    appApi.get("warehouses").then((d) => setWarehouses(d.warehouses || [])).catch(() => {});
  }, []);

  const cfg = BOOKING_MODES.find((m) => m.k === mode);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const switchMode = (k) => {
    setInternalMode(k);
    setQuotes(null);
    setBooking(null);
    setError("");
  };

  const getQuotes = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    setQuotes(null);
    setBooking(null);
    try {
      const body = {
        pickup_pincode: form.pickup_pincode,
        delivery_pincode: form.delivery_pincode,
        weight_kg: parseFloat(form.weight_kg),
      };
      if (mode === "b2c") body.payment_mode = form.payment_mode;
      const d = await appApi.post(cfg.endpoint, body);
      setQuotes(d.quotes || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const openBooking = (q) => {
    const wh = warehouses.find((w) => w.pincode === form.pickup_pincode) || warehouses[0];
    setBooking(q);
    setBookingError("");
    const pkg = { weight_kg: form.weight_kg, length_cm: "20", breadth_cm: "15", height_cm: "10", invoice_value: "", description: "" };
    const del = { ...EMPTY_ADDR, pincode: form.delivery_pincode };
    setB2c({ warehouse_id: wh?.id || "", delivery: del, package: pkg });
    setAir({ warehouse_id: wh?.id || "", delivery: del, package: { weight_kg: form.weight_kg, length_cm: "20", breadth_cm: "15", height_cm: "10", invoice_value: "" } });
    setB2b((s) => ({ ...s, warehouse_id: wh?.id || "", destination: { ...EMPTY_DEST, pincode: form.delivery_pincode }, units: [{ units: "1", weight: form.weight_kg, length: "40", width: "30", height: "30" }] }));
  };

  const whPayload = (id) => {
    const w = warehouses.find((x) => x.id === id);
    if (!w) return null;
    return {
      name: w.contact_name || w.name, phone: w.phone,
      address: [w.address_line_1, w.address_line_2].filter(Boolean).join(", "),
      city: w.city, state: w.state, pincode: w.pincode,
      warehouse_name: w.address_title, contact_name: w.contact_name || w.name, contact_phone: w.phone,
      address_line_1: w.address_line_1,
    };
  };

  const confirmBooking = async (e) => {
    e.preventDefault();
    setBookingError("");
    setBusy(true);
    try {
      let payload;
      if (mode === "b2c") {
        const w = whPayload(b2c.warehouse_id);
        if (!w) throw { response: { data: { detail: "Add a pickup warehouse first (Warehouses page)." } } };
        payload = {
          pickup: { name: w.name, phone: w.phone, address: w.address, city: w.city, state: w.state, pincode: w.pincode },
          delivery: b2c.delivery,
          package: {
            weight_kg: parseFloat(b2c.package.weight_kg), length_cm: parseFloat(b2c.package.length_cm),
            breadth_cm: parseFloat(b2c.package.breadth_cm), height_cm: parseFloat(b2c.package.height_cm),
            invoice_value: parseFloat(b2c.package.invoice_value), description: b2c.package.description,
          },
          courier_id: booking.courier_id,
          payment_mode: form.payment_mode,
        };
      } else if (mode === "air") {
        if (!air.warehouse_id) throw { response: { data: { detail: "Select a pickup warehouse." } } };
        payload = {
          warehouse_id: air.warehouse_id,
          delivery: air.delivery,
          package: {
            weight_kg: parseFloat(air.package.weight_kg), length_cm: parseFloat(air.package.length_cm),
            breadth_cm: parseFloat(air.package.breadth_cm), height_cm: parseFloat(air.package.height_cm),
            invoice_value: parseFloat(air.package.invoice_value),
          },
          sr_courier_id: booking.sr_courier_id,
          expected_rate: booking.rate,
        };
      } else {
        const w = whPayload(b2b.warehouse_id);
        if (!w) throw { response: { data: { detail: "Add a pickup warehouse first (Warehouses page)." } } };
        payload = {
          source: { warehouse_name: w.warehouse_name, contact_name: w.contact_name, contact_phone: w.contact_phone, address_line_1: w.address_line_1, city: w.city, state: w.state, pincode: w.pincode },
          destination: b2b.destination,
          packaging_unit_details: b2b.units.map((u) => ({
            units: parseInt(u.units) || 1, weight: parseFloat(u.weight),
            length: parseFloat(u.length), width: parseFloat(u.width), height: parseFloat(u.height),
          })),
          invoice_value: parseFloat(b2b.invoice_value),
        };
      }
      const d = await appApi.post(cfg.bookEndpoint, payload);
      const awb = d.awb || d.order?.awb || "";
      toast.success(`Booked ${booking.display_name || booking.courier_name}${awb ? ` — AWB ${awb}` : ""}. Wallet debited.`);
      navigate("/dashboard/orders");
    } catch (err) {
      setBookingError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {!fixedMode && (
        <div className="flex gap-0 border-2 border-obsidian w-fit bg-white mb-6" data-testid="rates-mode-tabs">
          {BOOKING_MODES.map((m) => (
            <button key={m.k} onClick={() => switchMode(m.k)} data-testid={`rates-mode-${m.k}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${mode === m.k ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
              <m.icon className="w-3.5 h-3.5" /> {m.label}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={getQuotes} data-testid="rates-form" className="border-2 border-obsidian bg-white p-6 grid sm:grid-cols-4 gap-4 items-end">
        <div>
          <label htmlFor="rates-pickup" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Pickup pincode</label>
          <input id="rates-pickup" data-testid="rates-pickup-input" required maxLength={6} value={form.pickup_pincode} onChange={set("pickup_pincode")} placeholder="713101"
            className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
        </div>
        <div>
          <label htmlFor="rates-delivery" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Delivery pincode</label>
          <input id="rates-delivery" data-testid="rates-delivery-input" required maxLength={6} value={form.delivery_pincode} onChange={set("delivery_pincode")} placeholder="110001"
            className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="rates-weight" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{mode === "b2b" ? "Total wt (kg)" : "Weight (kg)"}</label>
            <input id="rates-weight" data-testid="rates-weight-input" required type="number" min="0.1" step="0.1" value={form.weight_kg} onChange={set("weight_kg")}
              className="mt-2 w-full border-2 border-obsidian bg-paper px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian" />
          </div>
          {mode === "b2c" ? (
            <div>
              <label htmlFor="rates-payment" className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Payment</label>
              <select id="rates-payment" data-testid="rates-payment-select" value={form.payment_mode} onChange={set("payment_mode")}
                className="mt-2 w-full border-2 border-obsidian bg-paper px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian">
                <option value="prepaid">Prepaid</option>
                <option value="cod">COD</option>
              </select>
            </div>
          ) : (
            <div className="flex items-end pb-1 text-[11px] font-mono text-zinc-400 uppercase tracking-widest">{mode === "air" ? "Blue Dart · DTDC" : "Surface cargo"}</div>
          )}
        </div>
        <button type="submit" data-testid="rates-submit-button" disabled={loading}
          className="inline-flex items-center justify-center gap-2 bg-obsidian text-sky-300 font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#2563EB] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          <Calculator className="w-4 h-4" /> {loading ? "Fetching…" : "Get Quotes"}
        </button>
      </form>

      {error && <div data-testid="rates-error" className="mt-6 border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{error}</div>}

      {quotes && (
        <div data-testid="rates-results" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {quotes.map((q) => <QuoteCard key={q.courier_id} q={q} onBook={openBooking} booking={booking} />)}
          {quotes.length === 0 && <p className="text-sm text-zinc-500 sm:col-span-2">No couriers serve this pincode pair right now.</p>}
        </div>
      )}

      {booking && (
        <form onSubmit={confirmBooking} data-testid="booking-form" className="mt-6 border-2 border-obsidian bg-white shadow-[8px_8px_0px_0px_#2563EB]">
          <div className="flex items-center justify-between px-6 py-4 border-b-2 border-obsidian bg-obsidian text-white">
            <p className="font-bold text-sm">
              Booking <span className="text-sky-300">{booking.display_name || booking.courier_name}</span> · {inr(booking.rate)} · {form.pickup_pincode} → {form.delivery_pincode}
              <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">{cfg.label}</span>
            </p>
            <button type="button" onClick={() => setBooking(null)} aria-label="Close booking" data-testid="booking-close" className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 grid sm:grid-cols-2 gap-6">
            {bookingError && (
              <div data-testid="booking-error" className="sm:col-span-2 border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">{bookingError}</div>
            )}

            {mode === "b2c" && (
              <>
                <div>
                  <WarehouseSelect warehouses={warehouses} value={b2c.warehouse_id} onChange={(e) => setB2c((s) => ({ ...s, warehouse_id: e.target.value }))} testid="booking-warehouse-select" />
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 mt-5">Package</p>
                  <div className="mt-2">
                    <PackageFields pkg={b2c.package} setPkg={(fn) => setB2c((s) => ({ ...s, package: fn(s.package) }))} prefix="booking-package" withDesc />
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Delivery (customer)</p>
                  <div className="mt-2">
                    <DeliveryFields state={b2c.delivery} setState={(fn) => setB2c((s) => ({ ...s, delivery: fn(s.delivery) }))} prefix="booking-delivery" />
                  </div>
                </div>
              </>
            )}

            {mode === "air" && (
              <>
                <div>
                  <WarehouseSelect warehouses={warehouses} value={air.warehouse_id} onChange={(e) => setAir((s) => ({ ...s, warehouse_id: e.target.value }))} testid="booking-air-warehouse-select" />
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 mt-5">Package</p>
                  <div className="mt-2">
                    <PackageFields pkg={air.package} setPkg={(fn) => setAir((s) => ({ ...s, package: fn(s.package) }))} prefix="booking-air-package" />
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Delivery (customer)</p>
                  <div className="mt-2">
                    <DeliveryFields state={air.delivery} setState={(fn) => setAir((s) => ({ ...s, delivery: fn(s.delivery) }))} prefix="booking-air-delivery" />
                  </div>
                </div>
              </>
            )}

            {mode === "b2b" && (
              <>
                <div>
                  <WarehouseSelect warehouses={warehouses} value={b2b.warehouse_id} onChange={(e) => setB2b((s) => ({ ...s, warehouse_id: e.target.value }))} testid="booking-b2b-warehouse-select" />
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 mt-5">Boxes / units</p>
                  <div className="space-y-3 mt-2" data-testid="booking-b2b-units">
                    {b2b.units.map((u, i) => (
                      <div key={i} className="border border-zinc-300 p-3 grid grid-cols-5 gap-2 items-end">
                        <Field label="Units" type="number" value={u.units} ph="1" testid={`b2b-unit-${i}-units`}
                          onChange={(e) => setB2b((s) => ({ ...s, units: s.units.map((x, j) => (j === i ? { ...x, units: e.target.value } : x)) }))} />
                        <Field label="Wt kg" type="number" value={u.weight} ph="10" testid={`b2b-unit-${i}-weight`}
                          onChange={(e) => setB2b((s) => ({ ...s, units: s.units.map((x, j) => (j === i ? { ...x, weight: e.target.value } : x)) }))} />
                        <Field label="L" type="number" value={u.length} ph="40" testid={`b2b-unit-${i}-length`}
                          onChange={(e) => setB2b((s) => ({ ...s, units: s.units.map((x, j) => (j === i ? { ...x, length: e.target.value } : x)) }))} />
                        <Field label="W" type="number" value={u.width} ph="30" testid={`b2b-unit-${i}-width`}
                          onChange={(e) => setB2b((s) => ({ ...s, units: s.units.map((x, j) => (j === i ? { ...x, width: e.target.value } : x)) }))} />
                        <div className="flex gap-1 items-end">
                          <Field label="H" type="number" value={u.height} ph="30" testid={`b2b-unit-${i}-height`}
                            onChange={(e) => setB2b((s) => ({ ...s, units: s.units.map((x, j) => (j === i ? { ...x, height: e.target.value } : x)) }))} />
                          {b2b.units.length > 1 && (
                            <button type="button" aria-label="Remove unit" data-testid={`b2b-unit-${i}-remove`}
                              onClick={() => setB2b((s) => ({ ...s, units: s.units.filter((_, j) => j !== i) }))}
                              className="p-2 border-2 border-obsidian text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    <button type="button" data-testid="b2b-add-unit"
                      onClick={() => setB2b((s) => ({ ...s, units: [...s.units, { units: "1", weight: "10", length: "40", width: "30", height: "30" }] }))}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest border-2 border-obsidian px-4 py-2 hover:bg-acid hover:text-white transition-colors duration-150">
                      <Plus className="w-3.5 h-3.5" /> Add box
                    </button>
                    <div>
                      <Field label="Invoice value (₹)" type="number" value={b2b.invoice_value} ph="5000" testid="b2b-invoice"
                        onChange={(e) => setB2b((s) => ({ ...s, invoice_value: e.target.value }))} />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Destination (buyer warehouse)</p>
                  <div className="space-y-3 mt-2">
                    {[
                      { k: "warehouse_name", ph: "Destination name / firm" }, { k: "contact_name", ph: "Contact person" },
                      { k: "contact_phone", ph: "Contact phone (10 digits)", max: 10 }, { k: "address_line_1", ph: "Address" },
                      { k: "city", ph: "City" }, { k: "state", ph: "State" }, { k: "pincode", ph: "Pincode", max: 6 },
                    ].map((f) => (
                      <Field key={f.k} label={f.ph} value={b2b.destination[f.k]} ph={f.ph} max={f.max} testid={`booking-b2b-dest-${f.k}`}
                        onChange={(e) => setB2b((s) => ({ ...s, destination: { ...s.destination, [f.k]: e.target.value } }))} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="px-6 py-4 border-t-2 border-obsidian flex items-center justify-between flex-wrap gap-3">
            <p className="font-mono text-xs text-zinc-500">
              Wallet will be debited <span className="font-bold text-white bg-acid px-2 py-0.5 border border-obsidian">{inr(booking.rate)}</span>
              {mode === "b2c" ? ` · ${form.payment_mode.toUpperCase()}` : mode === "b2b" ? " · FREIGHT" : " · AIR"}
            </p>
            <button type="submit" disabled={busy} data-testid="booking-submit"
              className="inline-flex items-center gap-2 bg-acid text-white font-bold px-8 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
              <PackagePlus className="w-4 h-4" /> {busy ? "Booking…" : "Confirm Booking"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
